// Single-picker answers have separate human and machine representations:
// labels belong in visible prose, while IDs/enum values ride in <ctx:…>
// markers. Keeping the split here prevents optimistic bubbles from exposing
// raw values before the server has a chance to process the turn.

import { collapseMarkerBackedSelectionIDs } from '../visibleContext';

const CTX_MARKER_RE = /<ctx:([a-z_][a-z0-9_]*)=([^>]*)>/gi;

// A machine value must never close or split its context marker.
const sanitizeMachineValue = (value: string): string =>
  value.replace(/[<>;\r\n]/g, ' ').trim();

export const choiceContextMarker = (rawValue: string): string => {
  const value = sanitizeMachineValue(rawValue);
  const namespaced = /^([a-z_][a-z0-9_]*):(.+)$/i.exec(value);
  if (namespaced && namespaced[1].toLowerCase() !== 'create') {
    return `<ctx:${namespaced[1].toLowerCase()}=${sanitizeMachineValue(
      namespaced[2]
    )}>`;
  }
  return `<ctx:choice=${value}>`;
};

export const buildChoiceMessage = (
  pending: string,
  value: string,
  label: string
): string => {
  const markerStart = pending.indexOf('<ctx:');
  const rawVisible = markerStart >= 0 ? pending.slice(0, markerStart) : pending;
  const existingMarkers =
    markerStart >= 0 ? pending.slice(markerStart).trim() : '';
  const markerValues = Array.from(existingMarkers.matchAll(CTX_MARKER_RE)).map(
    (match) => ({ key: match[1], value: match[2] })
  );
  const visibleTemplate = collapseMarkerBackedSelectionIDs(
    rawVisible.trim(),
    markerValues
  );

  // Keep the create:new sentinel visible for backwards-compatible
  // deterministic routing. It is an action token, not a record ID.
  if (/^create:new$/i.test(value.trim())) {
    const sentinel = `${value} (${label})`;
    const readable = visibleTemplate.includes('{value}')
      ? visibleTemplate.replace('{value}', sentinel)
      : `${visibleTemplate} ${sentinel}`;
    return [readable.trim(), existingMarkers].filter(Boolean).join('\n');
  }

  const readable = visibleTemplate.includes('{value}')
    ? visibleTemplate.replace('{value}', label)
    : `${visibleTemplate} ${label}`;
  const choiceMarker = choiceContextMarker(value);
  const markers = existingMarkers.includes(choiceMarker)
    ? existingMarkers
    : [existingMarkers, choiceMarker].filter(Boolean).join('\n');
  return [readable.trim(), markers].filter(Boolean).join('\n');
};
